/**
 * Protected Route Component
 *
 * Wrapper component that ensures user is authenticated before rendering
 */

import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import LoadingOverlay from './ui/LoadingOverlay';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loading, initializingBilling } = useAuth();

  // Show loading state while checking auth or initializing billing
  if (loading || initializingBilling) {
    const messages = loading
      ? ['Checking authentication...']
      : ['Setting up your account...', 'Initializing billing...'];

    return (
      <LoadingOverlay
        isVisible={true}
        altText='Loading'
        messages={messages}
        fullScreen={true}
      />
    );
  }

  // Redirect to referrer to show auth modal if not authenticated
  if (!isAuthenticated) {
    // Store the intended destination in sessionStorage
    sessionStorage.setItem('authReturnUrl', window.location.href);

    // Redirect to referrer page with auth modal trigger
    // SECURITY: Validate referrer is same-origin to prevent open redirect attacks
    const referrer = document.referrer || '/';
    const currentUrl = window.location.href;

    try {
      const referrerUrl = new URL(referrer, window.location.origin);
      const currentUrlObj = new URL(currentUrl);

      // Only redirect if referrer is same-origin
      if (referrerUrl.origin === window.location.origin) {
        // Check if referrer is the same as current location to avoid redirect loops
        if (referrerUrl.pathname === currentUrlObj.pathname) {
          // Redirect to home if we're in a loop
          const homeUrl = new URL('/', window.location.origin);
          homeUrl.searchParams.set('auth', 'true');
          window.location.href = homeUrl.toString();
        } else {
          // Properly set the auth parameter using URLSearchParams
          referrerUrl.searchParams.set('auth', 'true');
          window.location.href = referrerUrl.toString();
        }
      } else {
        // Fallback to home page if referrer is external
        const homeUrl = new URL('/', window.location.origin);
        homeUrl.searchParams.set('auth', 'true');
        window.location.href = homeUrl.toString();
      }
    } catch {
      // If referrer is not a valid URL, redirect to home
      const homeUrl = new URL('/', window.location.origin);
      homeUrl.searchParams.set('auth', 'true');
      window.location.href = homeUrl.toString();
    }
    return null;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
